/**
 * One version of a policy's file, on its own page: the PDF in the browser's
 * viewer, with who uploaded it, when, and why, and a way back to the policy.
 * Opened from the Versions tab; the header menu's per-version downloads are
 * the shortcut for people who only want the bytes.
 *
 * `$policyId_` (trailing underscore) keeps this route a sibling of the detail
 * page rather than a child rendered into it -- the detail page has no Outlet.
 *
 * @module routes/$org.compliance.policies.$policyId_.versions.$version
 */
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, DownloadIcon, ExternalLinkIcon, FileWarningIcon } from "lucide-react";

import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { PolicyDocumentTab } from "../components/regulation/policy/PolicyDocumentTab";
import { versionRows } from "../components/regulation/policy/PolicyVersionsTab";
import { formatDate } from "../components/regulation/policy/PolicyGovernanceTabs";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { Button } from "../components/ui/button";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { policyDisplayName, usePolicies } from "../state/policies";

export const Route = createFileRoute("/$org/compliance/policies/$policyId_/versions/$version")({
  component: PolicyVersionRoute,
});

function PolicyVersionRoute() {
  const { org: orgSegment, policyId, version: versionParam } = Route.useParams();
  const { org } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { snapshot, isPending, download, previewUrl } = usePolicies(organizationId);

  const policy = snapshot?.policies.find((candidate) => candidate.id === policyId) ?? null;
  const version = Number(versionParam);
  const rows = policy === null ? [] : versionRows(policy);
  const row =
    policy === null || !Number.isInteger(version)
      ? null
      : (rows.find((candidate) => candidate.version === version) ?? null);
  // This version's window: from its upload until the next one's. Processing
  // runs inside it are this file's runs.
  const successor = rows.find((candidate) => candidate.version === version + 1) ?? null;
  const src =
    policy === null || row === null
      ? null
      : previewUrl(policy.id, row.current ? undefined : row.version);

  const back = (
    <Link
      to="/$org/compliance/policies/$policyId"
      params={{ org: orgSegment, policyId }}
      className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeftIcon className="size-3.5" />
      {policy ? policyDisplayName(policy) : "Back to policy"}
    </Link>
  );

  if (policy === null || row === null || src === null) {
    return (
      <RegulationPageShell eyebrow={back} title={`Version ${versionParam}`}>
        {() =>
          isPending && snapshot === null ? (
            <ListSkeleton rows={4} />
          ) : (
            <div className="mt-6 rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
              <FileWarningIcon className="mx-auto size-5 text-muted-foreground/60" />
              <p className="mt-3 text-sm font-medium text-foreground">Version not found</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                This policy has no version {versionParam}.
              </p>
            </div>
          )
        }
      </RegulationPageShell>
    );
  }

  return (
    <RegulationPageShell
      eyebrow={back}
      title={`${policyDisplayName(policy)} · v${row.version}`}
      titleAside={
        row.current ? (
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            Current
          </span>
        ) : (
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
            Superseded
          </span>
        )
      }
      description={row.note ?? undefined}
      meta={
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
          <span className="flex items-baseline gap-1.5">
            <span className="text-xs text-muted-foreground">File</span>
            <span className="text-foreground">{row.filename}</span>
          </span>
          <span className="flex items-baseline gap-1.5">
            <span className="text-xs text-muted-foreground">Uploaded</span>
            <span className="text-foreground">
              {formatDate(row.uploadedAt)}
              {row.uploadedByEmail ? ` by ${row.uploadedByEmail}` : ""}
            </span>
          </span>
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="cursor-pointer gap-1.5"
            render={<a href={src} target="_blank" rel="noopener noreferrer" />}
          >
            <ExternalLinkIcon className="size-3.5" />
            Open in new tab
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="cursor-pointer gap-1.5"
            onClick={() => download(policy.id, row.current ? undefined : row.version)}
          >
            <DownloadIcon className="size-3.5" />
            Download
          </Button>
        </div>
      }
    >
      {() => (
        <div className="mt-6 flex flex-col gap-6">
          {/* What used to be the policy page's Source tab, scoped to this
              file: its processing runs, stats and settings — first, because
              that is what this page adds; the PDF itself follows. */}
          <PolicyDocumentTab
            organizationId={organizationId}
            policyId={policy.id}
            orgSegment={orgSegment}
            scope={{
              version: row.version,
              current: row.current,
              from: row.uploadedAt,
              to: successor?.uploadedAt ?? null,
            }}
          />
          <iframe
            src={src}
            title={`${row.filename} (v${row.version})`}
            className="h-[max(480px,calc(100vh-260px))] w-full rounded-xl border border-border/60 bg-muted"
          />
        </div>
      )}
    </RegulationPageShell>
  );
}
